"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, Music, Wine, Sparkles } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheAfterPartySpotlight() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  return <section id="the-after-party-spotlight" className="dark bg-background text-foreground relative w-full min-h-[80vh] overflow-hidden flex items-center justify-center py-24">
      {/* Background Video Layer */}
      <div className="absolute inset-0 z-0 select-none pointer-events-none">
        {hasMounted && <video src="/videos/after-party-loop.mp4" autoPlay loop muted playsInline className="object-cover w-full h-full opacity-60" style={{
        objectFit: "cover",
        position: "absolute",
        top: 0,
        left: 0
      }} />}
        {/* Dark overlay for better text contrast */}
        <div className="absolute inset-0 bg-sidebar/80 mix-blend-multiply" />
      </div>

      {/* Gradient Orbs for Depth */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        {/* Top-right pink/red orb */}
        <div className="absolute -top-[20%] -right-[10%] w-[60vw] h-[60vw] bg-chart-3/30 blur-[120px] rounded-full mix-blend-screen animate-pulse duration-[4000ms]" />
        
        {/* Bottom-left orange orb */}
        <div className="absolute -bottom-[20%] -left-[10%] w-[50vw] h-[50vw] bg-chart-4/20 blur-[100px] rounded-full mix-blend-screen animate-pulse duration-[5000ms]" />
        
        {/* Mid-level purple accent orb */}
        <div className="absolute top-[40%] left-[20%] w-[20vw] h-[20vw] bg-purple-500/20 blur-[80px] rounded-full mix-blend-overlay" />
      </div>

      {/* Main Content */}
      <div ref={sectionRef} className={`relative z-10 container mx-auto px-6 text-center transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        {/* Icon Badge */}
        <div className="flex justify-center mb-6">
          <div className="p-4 rounded-full bg-white/5 backdrop-blur-md border border-white/10 shadow-[0_0_30px_rgba(255,255,255,0.1)]">
            <Sparkles className="w-8 h-8 text-chart-4" />
          </div>
        </div>

        {/* Main Headline */}
        <h2 className="text-5xl md:text-7xl lg:text-8xl font-serif font-bold italic tracking-tighter mb-6 text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-white/70 drop-shadow-2xl">
          THE AFTER PARTY
        </h2>

        {/* Subheadline */}
        <p className="text-xl md:text-2xl font-sans text-white/90 max-w-3xl mx-auto mb-10 leading-relaxed font-light">
          The night doesn&apos;t end when the main event closes. Transition into an electric atmosphere of high-energy beats, signature mixology, and uninhibited celebration.
        </p>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
          {[{
          icon: Music,
          label: "Live DJ Sets",
          desc: "International Talent"
        }, {
          icon: Wine,
          label: "Ultra Lounge",
          desc: "Premium Open Bar"
        }, {
          icon: Sparkles,
          label: "VIP Access",
          desc: "Exclusive Areas"
        }].map((item, i) => <div key={i} data-index={i} className="flex flex-col items-center p-4 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors">
              <item.icon className="w-6 h-6 text-chart-3 mb-2" />
              <span className="font-serif text-lg font-semibold text-white">{item.label}</span>
              <span className="text-sm text-white/60">{item.desc}</span>
            </div>)}
        </div>

        {/* Call-to-Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild size="lg" className="bg-chart-3 hover:bg-chart-3/90 text-white border-none shadow-[0_0_20px_rgba(219,39,119,0.4)] px-8 py-6 text-lg rounded-full">
            <Link to="/tickets">
              Get Party Access
            </Link>
          </Button>
          
          <Button asChild variant="outline" size="lg" className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white px-8 py-6 text-lg rounded-full backdrop-blur-sm">
            <Link to="/experience">
              View Experience
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}